import * as React from 'react'
import { useToast } from '~/hooks/useToast'

export type DisabledReason =
  | { type: 'not_ready'; message?: string }
  | { type: 'generating'; message?: string }
  | { type: 'validation_error'; message: string }
  | { type: 'custom'; message: string }

export interface UseClickWithDisabledReasonOptions {
  disabled: boolean
  ariaDisabled?: boolean
  reason?: DisabledReason | null
  onClick: () => void
}

export const useClickWithDisabledReason = (
  options: UseClickWithDisabledReasonOptions
) => {
  const { disabled, ariaDisabled = false, reason = null, onClick } = options
  const toast = useToast()

  const handleClick = React.useCallback(() => {
    if (disabled) {
      return
    }
    if (ariaDisabled && reason) {
      const title = (() => {
        switch (reason.type) {
          case 'not_ready':
            return 'まだ完了していません'
          case 'generating':
            return '処理中のため操作できません'
          case 'validation_error':
            return '入力内容を確認してください'
          case 'custom':
            return '操作できません'
        }
      })()

      toast.show({
        variant: 'info',
        title,
        description: reason.message,
        duration: 6000
      })
      return
    }
    onClick()
  }, [disabled, ariaDisabled, reason, onClick, toast.show])

  const buttonProps = React.useMemo(() => {
    return {
      disabled,
      'aria-disabled': ariaDisabled ? 'true' : undefined,
      onClick: handleClick
    } as const
  }, [disabled, ariaDisabled, handleClick])

  return { onClick: handleClick, buttonProps }
}
